import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateRestoSlug } from "../utils/RestoSlugUtils";
import LoadingIndicator from "./LoadingIndicator";
import { CloseIcon, ShareIcon } from "./Icon";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { useSidebarState } from "../hooks/useSidebarState";
import AnalyticsManager from "../utils/AnalyticsManager";
import RestoApiService, { ChainDetailResponse } from "../utils/RestoApiService";
import styles from "./RestaurantDetailsSidebar.module.css";

interface ChainContentProps {
  chainSlug?: string;
  initialData?: ChainDetailResponse;
}

type SortMode = "postal" | "scoreAsc" | "scoreDesc";

const getScoreEmoji = (score: number) => {
  if (score === 5) return "😍";
  if (score === 4) return "😃";
  if (score === 3) return "🙂";
  if (score === 2) return "🤮";
  if (score === 1) return "💀";
  return "❓";
};

const getScoreLabel = (score: number, t: (key: string, fallback: string) => string) => {
  if (score >= 4) return t('score.excellent', 'Excellent');
  if (score === 3) return t('score.good', 'Good');
  if (score === 2) return t('score.poor', 'Poor');
  if (score === 1) return t('score.bad', 'Very Poor');
  return t('score.unknown', 'Unknown');
};

const getScoreColor = (score: number) => {
  if (score >= 4) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700/50';
  if (score === 3) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700/50';
  if (score >= 1) return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700/50';
  return 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
};

const DISTRIBUTION_BAR_COLORS: Record<number, string> = {
  1: 'bg-red-500',
  2: 'bg-orange-500',
  3: 'bg-yellow-500',
  4: 'bg-lime-500',
  5: 'bg-green-500',
};

const restoApiService = new RestoApiService();

const ChainContent: React.FC<ChainContentProps> = ({ chainSlug, initialData }) => {
  const { t, i18n } = useTranslation();
  const { closeSidebar } = useSidebarState(sidebarStateManager);
  const [chain, setChain] = useState<ChainDetailResponse | null>(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [sortMode, setSortMode] = useState<SortMode>("postal");
  const lang = i18n.language || 'en';

  useEffect(() => {
    if (initialData || !chainSlug) return;
    const fetchChain = async () => {
      setLoading(true);
      const result = await restoApiService.fetchChainDetail(chainSlug);
      if (result.success && result.data) setChain(result.data);
      setLoading(false);
    };
    fetchChain();
  }, [chainSlug, initialData]);

  // Dynamic title/description + page-view tracking once the chain resolves
  useEffect(() => {
    if (!chain) return;
    const isNL = lang === 'nl';
    const isFR = lang === 'fr';
    document.title = isNL
      ? `${chain.name}: hygiënescores van ${chain.locationCount} vestigingen | Kauwen of Spauwen`
      : isFR
        ? `${chain.name} : scores d'hygiène de ${chain.locationCount} établissements | Kauwen of Spauwen`
        : `${chain.name}: hygiene scores of ${chain.locationCount} locations | Kauwen of Spauwen`;

    AnalyticsManager.getInstance().trackEvent('chain_page_view', {
      chain_slug: chain.slug,
      location_count: chain.locationCount,
      event_category: 'content',
    });
  }, [chain, lang]);

  const sortedLocations = useMemo(() => {
    if (!chain) return [];
    const list = [...chain.locations];
    if (sortMode === 'scoreAsc') list.sort((a, b) => a.inspectionForks - b.inspectionForks);
    else if (sortMode === 'scoreDesc') list.sort((a, b) => b.inspectionForks - a.inspectionForks);
    // 'postal' keeps the server order (postal code, then name)
    return list;
  }, [chain, sortMode]);

  const getRestoUrl = (id: number, name: string) => `/${lang}/restaurant/${generateRestoSlug(name, id)}`;

  const handleLocationClick = (e: React.MouseEvent, id: number, name: string) => {
    e.preventDefault();
    AnalyticsManager.getInstance().trackEvent('chain_location_click', {
      chain_slug: chain?.slug,
      restaurant_id: id,
      event_category: 'navigation',
    });
    window.dispatchEvent(new CustomEvent('restaurantSelected', {
      detail: { restaurantId: id.toString(), openSidebar: true }
    }));
    window.history.pushState({}, '', getRestoUrl(id, name));
  };

  const handleShare = () => {
    if (!chain) return;
    AnalyticsManager.getInstance().trackEvent('chain_share', {
      chain_slug: chain.slug,
      event_category: 'engagement',
    });
    const url = window.location.href;
    const text = t('chain.shareText', '{{name}}: average FAVV score {{avg}}/5 across {{count}} locations', {
      name: chain.name,
      avg: chain.avgScore?.toFixed(1) ?? '—',
      count: chain.locationCount,
    });
    if (navigator.share) {
      navigator.share({ title: chain.name, text, url }).catch(() => { /* dismissed */ });
    } else {
      navigator.clipboard?.writeText(`${text} ${url}`);
    }
  };

  const shortAddress = (addr: string) => addr.split(/\d{4}/)[0].trim();

  const sortOptions: { mode: SortMode; label: string }[] = [
    { mode: 'postal', label: t('chain.sortCity', 'By area') },
    { mode: 'scoreDesc', label: t('chain.sortScoreDesc', 'Score: high → low') },
    { mode: 'scoreAsc', label: t('chain.sortScoreAsc', 'Score: low → high') },
  ];

  const distributionMax = chain
    ? Math.max(chain.distribution.score1, chain.distribution.score2, chain.distribution.score3, chain.distribution.score4, chain.distribution.score5, 1)
    : 1;

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`}>
        <div className="pl-4 pr-2 pt-4 pb-16">
          {/* Header */}
          <div className="flex justify-between items-center mb-1">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 truncate">
                {chain?.name || ''}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('chain.pageSubtitle', 'Locations sharing this name in Belgium')}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {chain && (
                <button
                  onClick={handleShare}
                  className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
                  aria-label={t('chain.share', 'Share')}
                >
                  <ShareIcon />
                </button>
              )}
              <button
                onClick={closeSidebar}
                className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingIndicator isLoading={true} />
            </div>
          )}

          {!loading && !chain && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium mb-2">
                {t('chain.noData', 'No data found for this chain')}
              </p>
            </div>
          )}

          {!loading && chain && (
            <>
              {/* Average score card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-4 mt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                      {chain.avgScore != null ? chain.avgScore.toFixed(1) : '—'}
                      <span className="text-lg text-gray-400 font-normal">/5</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('chain.avgExplain', 'average FAVV score across {{count}} inspected locations', { count: chain.scoredCount })}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {t('chain.scoredPct', '{{pct}}% of locations have a recent inspection score', { pct: chain.scoredPct })}
                    </p>
                  </div>
                  <div className="text-4xl">
                    {getScoreEmoji(Math.round(chain.avgScore ?? 0))}
                  </div>
                </div>
              </div>

              {/* Score distribution */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-4">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  {t('chain.distribution', 'Score distribution')}
                </h4>
                <div className="space-y-1.5">
                  {[5, 4, 3, 2, 1].map((score) => {
                    const count = chain.distribution[`score${score}` as keyof typeof chain.distribution];
                    return (
                      <div key={score} className="flex items-center gap-2">
                        <span className="text-sm w-7 flex-shrink-0">{getScoreEmoji(score)}</span>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${DISTRIBUTION_BAR_COLORS[score]}`}
                            style={{ width: `${(count / distributionMax) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right flex-shrink-0">{count}</span>
                      </div>
                    );
                  })}
                </div>
                {chain.distribution.unscored > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    {t('chain.unscored', '{{count}} locations without a recent score', { count: chain.distribution.unscored })}
                  </p>
                )}
              </div>

              {/* Locations */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('chain.locations', 'Locations ({{count}})', { count: chain.locationCount })}
                  </h4>
                </div>
                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {sortOptions.map(({ mode, label }) => (
                    <button
                      key={mode}
                      onClick={() => {
                        setSortMode(mode);
                        AnalyticsManager.getInstance().trackEvent('chain_sort_change', {
                          chain_slug: chain.slug,
                          sort: mode,
                          event_category: 'engagement',
                        });
                      }}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        sortMode === mode
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="space-y-1.5">
                  {sortedLocations.map((loc) => (
                    <a
                      key={loc.id}
                      href={getRestoUrl(loc.id, loc.name)}
                      onClick={(e) => handleLocationClick(e, loc.id, loc.name)}
                      className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors flex justify-between items-center gap-2 no-underline"
                    >
                      <div className="min-w-0 flex-1">
                        <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{loc.name}</h5>
                        <p className="text-xs text-gray-400 truncate">
                          {loc.postalCode ? `${loc.postalCode} · ` : ''}{shortAddress(loc.address)}
                        </p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${getScoreColor(loc.inspectionForks)}`}>
                        {getScoreLabel(loc.inspectionForks, t)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Data-driven prose for SEO uniqueness */}
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
                {t('chain.prose.overview', '{{name}} has {{count}} locations in our database, {{scored}} of which have a recent FAVV inspection score. The average score is {{avg}} out of 5.', {
                  name: chain.name,
                  count: chain.locationCount,
                  scored: chain.scoredCount,
                  avg: chain.avgScore != null ? chain.avgScore.toFixed(1) : '—',
                })}
              </p>

              {/* Methodology / legal framing */}
              <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                {t('chain.methodology', 'Averages are based on official FAVV inspection results per location. Locations are grouped by name and may belong to different operators.')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChainContent;
